import { fabric } from 'fabric';
import BoundingBox from "./canvas-objects/BoundingBox";
import CanvasObject from "./canvas-objects/CanvasObject";
import CanvasDebug from "./../CanvasDebug";
import apiRequestHelper from "../api/requestHelper";
import AppDebug from "../AppDebug";
import TextObject from "./canvas-objects/TextObject";
import ImageObject from "./canvas-objects/ImageObject";
import FontFaceObserver from 'fontfaceobserver';
import TextboxObject from "./canvas-objects/TextboxObject";
import MaxWidthTextObject from "./canvas-objects/MaxWidthTextObject";
import ImageLimitedFormObject from "./canvas-objects/ImageLimitedFormObject";
import ImageGalleryObject from "./canvas-objects/ImageGalleryObject";
import configuration from "../configuration/Configuration";

class Canvas {

    /**
     * @type fabric.Canvas
     */
    _canvas;

    /**
     * ID of Product View.
     * @type number
     */
    _viewId;

    /**
     * ID in database, only for identification.
     * @type number
     */
    _onlineId;

    /**
     * The bounding box represents the printing area on the product preview.
     * @type BoundingBox
     */
    _boundingBox;

    /**
     * Factor with which values can be converted from pixels to millimeters.
     * @type number
     */
    _factor;

    /**
     * Used to check the usability of an image file.
     * @type number
     */
    _dpi;

    /**
     * Array of objects that are mapped into FabricObjects and drawn on the canvas.
     * @type CanvasObject[]
     */
    _canvasObjects;

    /**
     * Currently on canvas selected objects. Array of Ids.
     * @type {string[]}
     */
    _currentSelectedObjects;

    /**
     * Last on canvas selected objects. Array of Ids.
     * @type {string[]}
     */
    _lastSelectedObjects;

    /**
     * @type {Promise<fabric.Image>}
     */
    _promiseBackgroundImage;

    _canAddElements;
    _canSortElements;

    /**
     * @type {Array<Object>}
     */
    _createableObjectTypes;

    /**
     * @type {string}
     */
    _articleNumber;

    /**
     * Create a Canvas Instance.
     * @param {string} backgroundImageUrl Asyncron creates an object of type fabric.Image in the background from the given url.
     * @param {number} viewId
     * @param {number} dpi
     * @param {number} factor
     * @param {?number} onlineId id if exists in db
     */
    constructor(backgroundImageUrl, viewId, dpi, factor, onlineId = null) {

        this._promiseBackgroundImage = this.loadBackgroundImage(backgroundImageUrl);
        this._viewId = viewId;
        this._lastSelectedObjects = [];
        this._currentSelectedObjects = [];
        this._canvasObjects = [];
        this._createableObjectTypes = [];
        this._onlineId = onlineId;
        this._dpi = dpi;
        this._factor = factor;

        this.setCanAddElements(true);
        this.setCanSortElements(true);

    }

    /**
     * Create a fabric.js Canvas.
     * @param {string} cssSelectorOfCanvas
     * @param {number} width
     * @param {number} height
     * @param {BoundingBox} boundingBox
     * @param {Array} canvasObjects Observable array from vue.js data.
     */
    createCanvas(cssSelectorOfCanvas, width, height, boundingBox, canvasObjects) {

        CanvasDebug.canvas.created({ params: { cssSelectorOfCanvas, width, height, boundingBox, canvasObjects }, canvasInstance: this });

        this._canvas = new fabric.Canvas(cssSelectorOfCanvas, { width, height });
        if (boundingBox instanceof BoundingBox)
            this._boundingBox = boundingBox;
        else
            throw `The constructor of the Canvas class expects a BoundingBox object. Instead, the pass value is of type ${typeof boundingBox}.`;
        // this._canvasObjects = canvasObjects;

    }

    /**
     * Searches for a FabricObject on the canvas with a defined identifier.
     * @param {string} id
     * @returns {fabric.Object} FabricObject
     */
    getFabricObjectById(id) {

        return this.getCanvas().getObjects().find(fabricObject => fabricObject.id === id);

    }

    /**
     * Add new CanvasObject.
     * @param {CanvasObject} canvasObject
     * @return {boolean}
     */
    addCanvasObject(canvasObject) {

        if (canvasObject instanceof CanvasObject) {

            // inform the object that it has been added to a canvas
            canvasObject.eventAddedToCanvas(this);

            this.getCanvasObjects().push(canvasObject);
            return true;
        }

        return false;

    }

    /**
     * Remove a CanvasObject from this Canvas instance.
     * @param {string} canvasObjectId
     */
    removeCanvasObjectById(canvasObjectId) {

        const index = this.getCanvasObjects().findIndex(canvasObject => canvasObject.getId() === canvasObjectId);

        this.getCanvasObjects().splice(index, 1);

        const indexCurrent = this.getCanvasObjects().findIndex(canvasObject => canvasObject.getId() === canvasObjectId);
        const indexLast = this.getCanvasObjects().findIndex(canvasObject => canvasObject.getId() === canvasObjectId);

        if (indexCurrent)
            this.getCurrentSelectedObjects().splice(indexCurrent, 1);
        if (indexLast)
            this.getLastSelectedObjects().splice(indexLast, 1);

    }

    removeCurrentSelection() {

        this.getCanvas().discardActiveObject();
        this.getCanvas().renderAll();

        this.setLastSelectedObjects([]);

    }

    /**
     * Clear the  canvas and render new with the information from Vue.js state.
     * @returns {Promise<void>}
     */
    async render() {

        if (! this.getCanvas())
            return;

        // Removes all objects from the canvas.
        this.getCanvas().clear();

        // Creates new FabricObjects from all CanvasObjects and adds them to the canvas.
        // await this.getCanvasObjects().forEach(
        //     async canvasObject => {
        //         const createdCanvasObject = await canvasObject.createFabricObject();
        //         if (createdCanvasObject)
        //             this.getCanvas().add( createdCanvasObject )
        //     }
        // );

        for (const canvasObject of this.getCanvasObjects()) {
            const createdCanvasObject = await canvasObject.createFabricObject();
            if (createdCanvasObject)
                this.getCanvas().add( createdCanvasObject )
        }


        // If previously one object was selected recreate this selection.
        if (this.getLastSelectedObjects().length === 1) {
            this.getCanvas().setActiveObject(
                this.getFabricObjectById( this.getLastSelectedObjects()[0] )
            );
        }

        // If more than one object was previously selected create new multi selection.
        if (this.getLastSelectedObjects().length > 1) {
            this.getCanvas().setActiveObject(
                new fabric.ActiveSelection(
                    this.getLastSelectedObjects().map(id => this.getFabricObjectById(id)), { canvas: this.getCanvas() }
                )
            );
        }

        // Add the BoundingBox to the Canvas. The BoundingBox is positioned behind all other objects.
        if (this.getBoundingBox()) {
            const fabricBoundingBox = await this.getBoundingBox().createFabricObject();

            if ( Array.isArray(fabricBoundingBox) ) {
                // element 0 is the bounding box
                this.getCanvas().add(fabricBoundingBox[0]);
                this.getCanvas().bringToFront(fabricBoundingBox[0]);

                // element 1 is the overlay
                this.getCanvas().add(fabricBoundingBox[1]);
                this.getCanvas().bringToFront(fabricBoundingBox[1]);
            } else {
                this.getCanvas().add(fabricBoundingBox);
                this.getCanvas().bringToFront(fabricBoundingBox);
            }
        }


        // Clear cache for last selected objects.
        this.setLastSelectedObjects([]);

        // Set background image of canvas.
        this.getCanvas().setBackgroundImage(
            await this._promiseBackgroundImage
        );

        // Render canvas.
        this.getCanvas().renderAll();

    }

    /**
     * Loads background image for canvas.
     * @param {string} url
     * @returns {Promise<fabric.Image>}
     */
    async loadBackgroundImage(url) {

        const image = await new Promise(resolve => fabric.Image.fromURL(url, resolve));

        image.set({
            scaleX: this.getWidth() / image.width,
            scaleY: this.getHeight() / image.height
        });

        return image;

    }

    /**
     * Defines the event handlers for the canvas.
     */
    initEventHandler() {

        const mouseState = {

            isDown: false,
            downPosition: null,
            upPosition: null,
            isZoom: null

        };

        this.getCanvas().on( 'selection:created', event => this.eventSelectionCreated(event) );
        this.getCanvas().on( 'selection:cleared', event => this.eventSelectionCleared(event) );
        this.getCanvas().on( 'selection:updated', event => this.eventSelectionUpdated(event) );
        this.getCanvas().on( 'object:modified', event => this.eventCanvasObjectsModified(event) );
        this.getCanvas().on( 'mouse:wheel', event => this.eventScrollToZoom(event) );
        this.getCanvas().on( 'mouse:down', event => {
            mouseState.downPosition = event.pointer;
            mouseState.isDown = true;
        } );
        this.getCanvas().on( 'mouse:up', event => this.eventMouseUp(event, mouseState) );
    }

    /**
     * Creates objects of type CanvasObject and adds them to its own _canvasObjects property of type Array.
     * @param {Array<ResponseCanvasObject>} canvasObjectsFromResponse
     */
    createCanvasObjectsFromAPIResponse(canvasObjectsFromResponse) {

        console.log('schould create objects', canvasObjectsFromResponse);

        canvasObjectsFromResponse.forEach(async canvasObject => {

            let newCanvasObject = null;

            if (canvasObject.serializedObject.type === TextObject.TYPE_TITLE || canvasObject.serializedObject.type === MaxWidthTextObject.TYPE_TITLE) {

                if (canvasObject.serializedObject.type === MaxWidthTextObject.TYPE_TITLE) {
                    newCanvasObject = new MaxWidthTextObject(this, canvasObject.serializedObject.text.value);
                } else {
                    newCanvasObject = new TextObject(this, canvasObject.serializedObject.text.value);
                }

                newCanvasObject.set({
                    isSelectable: canvasObject.serializedObject.isSelectable,

                    label: canvasObject.serializedObject.label ? canvasObject.serializedObject.label.value : null,
                    labelSecondLanguage: canvasObject.serializedObject.labelSecondLanguage ? canvasObject.serializedObject.labelSecondLanguage.value : null,

                    left: canvasObject.serializedObject.left.value,
                    leftIsManipulable: canvasObject.serializedObject.left.manipulable,
                    leftWhitelist: canvasObject.serializedObject.left.whitelist,

                    leftBoundingBox: canvasObject.serializedObject.canvasRelativeLeft.value,
                    leftBoundingBoxIsManipulable: canvasObject.serializedObject.canvasRelativeLeft.manipulable,
                    leftBoundingBoxWhitelist: canvasObject.serializedObject.canvasRelativeLeft.whitelist,

                    top: canvasObject.serializedObject.top.value,
                    topIsManipulable: canvasObject.serializedObject.top.manipulable,
                    topWhitelist: canvasObject.serializedObject.top.whitelist,

                    topBoundingBox: canvasObject.serializedObject.canvasRelativeTop.value,
                    topBoundingBoxIsManipulable: canvasObject.serializedObject.canvasRelativeTop.manipulable,
                    topBoundingBoxWhitelist: canvasObject.serializedObject.canvasRelativeTop.whitelist,

                    exceedsBoundingBox: canvasObject.serializedObject.exceedsBoundingBox,
                    scaleX: canvasObject.serializedObject.scaleX,
                    scaleY: canvasObject.serializedObject.scaleY,

                    widthIsManipulable: canvasObject.serializedObject.width.manipulable,
                    widthWhitelist: canvasObject.serializedObject.width.whitelist,

                    heightIsManipulable: canvasObject.serializedObject.height.manipulable,
                    heightWhitelist: canvasObject.serializedObject.height.whitelist,

                    angle: canvasObject.serializedObject.angle.value,
                    angleIsManipulable: canvasObject.serializedObject.angle.manipulable,
                    angleWhitelist: canvasObject.serializedObject.angle.whitelist,

                    fontSize: canvasObject.serializedObject.fontSize.value,
                    fontSizeIsManipulable: canvasObject.serializedObject.fontSize.manipulable,
                    fontSizeWhitelist: canvasObject.serializedObject.fontSize.whitelist,

                    fontFamily: canvasObject.serializedObject.fontFamily.value,
                    fontFamilyIsManipulable: canvasObject.serializedObject.fontFamily.manipulable,
                    fontFamilyWhitelist: canvasObject.serializedObject.fontFamily.whitelist,

                    fill: canvasObject.serializedObject.fill.value,
                    fillIsManipulable: canvasObject.serializedObject.fill.manipulable,
                    fillWhitelist: canvasObject.serializedObject.fill.whitelist,

                    zPosition: canvasObject.serializedObject.zPosition,
                    visibility: canvasObject.serializedObject.visibility,
                    deletable: canvasObject.serializedObject.deletable
                });

                if (canvasObject.serializedObject.type === MaxWidthTextObject.TYPE_TITLE) {
                    newCanvasObject.set({
                        maxWidth: canvasObject.serializedObject.maxWidth.value,
                        maxWidthIsManipulable: canvasObject.serializedObject.maxWidth.manipulable,
                    });
                }

                if (newCanvasObject.hasOwnProperty('_print')) {
                    newCanvasObject._print = canvasObject.serializedObject.hasOwnProperty('print') ? canvasObject.serializedObject.print : true;
                }

                if (canvasObject.serializedObject.fontFamily.value !== 'Times New Roman')
                    await new FontFaceObserver(canvasObject.serializedObject.fontFamily.value).load();

            } else if (canvasObject.serializedObject.type === TextboxObject.TYPE_TITLE) {

                newCanvasObject = new TextboxObject(this, canvasObject.serializedObject.text.value);

                newCanvasObject.set({
                    isSelectable: canvasObject.serializedObject.isSelectable,

                    label: canvasObject.serializedObject.label ? canvasObject.serializedObject.label.value : null,
                    labelSecondLanguage: canvasObject.serializedObject.labelSecondLanguage ? canvasObject.serializedObject.labelSecondLanguage.value : null,

                    textAlign: canvasObject.serializedObject.textAlign.value,
                    textAlignIsManipulable: canvasObject.serializedObject.textAlign.manipulable,

                    left: canvasObject.serializedObject.left.value,
                    leftIsManipulable: canvasObject.serializedObject.left.manipulable,
                    leftWhitelist: canvasObject.serializedObject.left.whitelist,

                    leftBoundingBox: canvasObject.serializedObject.canvasRelativeLeft.value,
                    leftBoundingBoxIsManipulable: canvasObject.serializedObject.canvasRelativeLeft.manipulable,
                    leftBoundingBoxWhitelist: canvasObject.serializedObject.canvasRelativeLeft.whitelist,

                    top: canvasObject.serializedObject.top.value,
                    topIsManipulable: canvasObject.serializedObject.top.manipulable,
                    topWhitelist: canvasObject.serializedObject.top.whitelist,

                    topBoundingBox: canvasObject.serializedObject.canvasRelativeTop.value,
                    topBoundingBoxIsManipulable: canvasObject.serializedObject.canvasRelativeTop.manipulable,
                    topBoundingBoxWhitelist: canvasObject.serializedObject.canvasRelativeTop.whitelist,

                    exceedsBoundingBox: canvasObject.serializedObject.exceedsBoundingBox,
                    scaleX: canvasObject.serializedObject.scaleX,
                    scaleY: canvasObject.serializedObject.scaleY,

                    width: canvasObject.serializedObject.width.value,
                    widthIsManipulable: canvasObject.serializedObject.width.manipulable,
                    widthWhitelist: canvasObject.serializedObject.width.whitelist,

                    heightIsManipulable: canvasObject.serializedObject.height.manipulable,
                    heightWhitelist: canvasObject.serializedObject.height.whitelist,

                    angle: canvasObject.serializedObject.angle.value,
                    angleIsManipulable: canvasObject.serializedObject.angle.manipulable,
                    angleWhitelist: canvasObject.serializedObject.angle.whitelist,

                    fontSize: canvasObject.serializedObject.fontSize.value,
                    fontSizeIsManipulable: canvasObject.serializedObject.fontSize.manipulable,
                    fontSizeWhitelist: canvasObject.serializedObject.fontSize.whitelist,

                    fontFamily: canvasObject.serializedObject.fontFamily.value,
                    fontFamilyIsManipulable: canvasObject.serializedObject.fontFamily.manipulable,
                    fontFamilyWhitelist: canvasObject.serializedObject.fontFamily.whitelist,

                    fill: canvasObject.serializedObject.fill.value,
                    fillIsManipulable: canvasObject.serializedObject.fill.manipulable,
                    fillWhitelist: canvasObject.serializedObject.fill.whitelist,

                    isMultilineEnabled: canvasObject.serializedObject.isMultilineEnabled,
                    zPosition: canvasObject.serializedObject.zPosition,
                    visibility: canvasObject.serializedObject.visibility,
                    deletable: canvasObject.serializedObject.deletable
                });

                if (newCanvasObject.hasOwnProperty('_print')) {
                    newCanvasObject._print = canvasObject.serializedObject.hasOwnProperty('print') ? canvasObject.serializedObject.print : true;
                }

                if (canvasObject.serializedObject.fontFamily.value !== 'Times New Roman')
                    await new FontFaceObserver(canvasObject.serializedObject.fontFamily.value).load();

            } else if (canvasObject.serializedObject.type === ImageObject.TYPE_TITLE || canvasObject.serializedObject.type === ImageLimitedFormObject.TYPE_TITLE || canvasObject.serializedObject.type === ImageGalleryObject.TYPE_TITLE) {

                if (canvasObject.serializedObject.type === ImageLimitedFormObject.TYPE_TITLE) {
                    newCanvasObject = new ImageLimitedFormObject(this, null, apiRequestHelper.createUrl(`/image/${canvasObject.image.id}`));
                } if (canvasObject.serializedObject.type === ImageGalleryObject.TYPE_TITLE) {
                    newCanvasObject = new ImageGalleryObject(this, null, apiRequestHelper.createUrl(`/image/${canvasObject.image.id}`));
                    newCanvasObject.setGalleryId(canvasObject.serializedObject.galleryId);
                } else {
                    newCanvasObject = new ImageObject(this, null, apiRequestHelper.createUrl(`/image/${canvasObject.image.id}`));
                }

                newCanvasObject.set({
                    isSelectable: canvasObject.serializedObject.isSelectable,

                    left: canvasObject.serializedObject.left.value,
                    leftIsManipulable: canvasObject.serializedObject.left.manipulable,
                    leftWhitelist: canvasObject.serializedObject.left.whitelist,

                    leftBoundingBox: canvasObject.serializedObject.canvasRelativeLeft.value,
                    leftBoundingBoxIsManipulable: canvasObject.serializedObject.canvasRelativeLeft.manipulable,
                    leftBoundingBoxWhitelist: canvasObject.serializedObject.canvasRelativeLeft.whitelist,

                    top: canvasObject.serializedObject.top.value,
                    topIsManipulable: canvasObject.serializedObject.top.manipulable,
                    topWhitelist: canvasObject.serializedObject.top.whitelist,

                    topBoundingBox: canvasObject.serializedObject.canvasRelativeTop.value,
                    topBoundingBoxIsManipulable: canvasObject.serializedObject.canvasRelativeTop.manipulable,
                    topBoundingBoxWhitelist: canvasObject.serializedObject.canvasRelativeTop.whitelist,

                    exceedsBoundingBox: canvasObject.serializedObject.exceedsBoundingBox,
                    scaleX: canvasObject.serializedObject.scaleX,
                    scaleY: canvasObject.serializedObject.scaleY,

                    angle: canvasObject.serializedObject.angle.value,
                    angleIsManipulable: canvasObject.serializedObject.angle.manipulable,
                    angleWhitelist: canvasObject.serializedObject.angle.whitelist,

                    width: canvasObject.serializedObject.width.value,
                    widthIsManipulable: canvasObject.serializedObject.width.manipulable,
                    widthWhitelist: canvasObject.serializedObject.width.whitelist,

                    height: canvasObject.serializedObject.height.value,
                    heightIsManipulable: canvasObject.serializedObject.height.manipulable,
                    heightWhitelist: canvasObject.serializedObject.height.whitelist,

                    filename: canvasObject.serializedObject.image.value,
                    imageIsManipulable: canvasObject.serializedObject.image.manipulable,

                    zPosition: canvasObject.serializedObject.zPosition,
                    visibility: canvasObject.serializedObject.visibility,
                    deletable: canvasObject.serializedObject.deletable,

                    minBoundingBoxLeft: canvasObject.serializedObject.positionLimits ? canvasObject.serializedObject.positionLimits.min.left : null,
                    minBoundingBoxTop: canvasObject.serializedObject.positionLimits ? canvasObject.serializedObject.positionLimits.min.top : null,
                    minBoundingBoxRight: canvasObject.serializedObject.positionLimits ? canvasObject.serializedObject.positionLimits.min.right : null,
                    minBoundingBoxBottom: canvasObject.serializedObject.positionLimits ? canvasObject.serializedObject.positionLimits.min.bottom : null,

                    maxBoundingBoxLeft: canvasObject.serializedObject.positionLimits ? canvasObject.serializedObject.positionLimits.max.left : null,
                    maxBoundingBoxTop: canvasObject.serializedObject.positionLimits ? canvasObject.serializedObject.positionLimits.max.top : null,
                    maxBoundingBoxRight: canvasObject.serializedObject.positionLimits ? canvasObject.serializedObject.positionLimits.max.right : null,
                    maxBoundingBoxBottom: canvasObject.serializedObject.positionLimits ? canvasObject.serializedObject.positionLimits.max.bottom : null
                });

                if (newCanvasObject.hasOwnProperty('_print')) {
                    newCanvasObject._print = canvasObject.serializedObject.hasOwnProperty('print') ? canvasObject.serializedObject.print : true;
                }

                if (canvasObject.serializedObject.label) {
                    if (canvasObject.serializedObject.label.value) {
                        newCanvasObject.setLabel(canvasObject.serializedObject.label.value);
                    }
                }

                if (canvasObject.serializedObject.labelSecondLanguage) {
                    if (canvasObject.serializedObject.labelSecondLanguage.value) {
                        newCanvasObject.setLabelSecondLanguage(canvasObject.serializedObject.labelSecondLanguage.value);
                    }
                }

                await newCanvasObject.createImageStore(true);

            }

            if (newCanvasObject) { this.addCanvasObject(newCanvasObject); this.render(); }

        });

    }

    /**
     * Save this ProductConfigurationView in database.
     * @param productConfigurationId
     */
    async save(productConfigurationId) {

        const createdProductConfigurationView = await apiRequestHelper.requestApiRoute(
            'post',
            `/configuration/${productConfigurationId}/view`,
            {
            boundingBoxX: this.getBoundingBox().getLeft(),
            boundingBoxY: this.getBoundingBox().getTop(),
            canAddElements: this.getCanAddElements(),
            canSortElements: this.getCanSortElements(),
            viewId: this.getViewId()
        }, (error) => { AppDebug.api.events.productConfigurationViewCreationFailed({error}) });

        if (! createdProductConfigurationView) {
            AppDebug.api.events.productConfigurationViewCreationFailed(`ProductConfigurationView with canvas ${this.getCanvas()} could not be saved successfully.`);
            return;
        }

        const objects = this.getCanvasObjects();

        for (let i = 0; i < objects.length; i++) {
            const object = objects[i];
            await object.save(createdProductConfigurationView.id);
        }

    }

    /**
     * A new selection was created with the mouse.
     * @param event
     */
    eventSelectionCreated(event) {

        this.setCurrentSelectedObjects(
            event.selected.map(fabricObject => fabricObject.id)
        );

        if (event.selected.length === 1) {
            if (event.selected[0] instanceof fabric.Image) {
                event.selected[0].set({opacity: 0.7});
            }
        }

        CanvasDebug.selection.created({
            event,
            description: 'New selection of objects created on the canvas.',
            currentSelectedObjects: this.getCurrentSelectedObjects()
        });

    }

    /**
     * There are no more objects selected on the canvas.
     * @param event
     */
    eventSelectionCleared(event) {

        // if selection cleared programmatically then the e property is undefined, when user active deselects then e is the mouse click event
        // only cache old selection if programmatically deselected by modify objects on canvas
        if (event.e === undefined) {
            this.setLastSelectedObjects( this.getCurrentSelectedObjects() );
        } else {
            this.setLastSelectedObjects([]);
            this.render();
        }
        this.setCurrentSelectedObjects([]);

        CanvasDebug.selection.cleared({
            event,
            description: 'There is no object selected on the canvas.',
            currentSelectedObjects: this.getCurrentSelectedObjects()
        });

    }

    /**
     * There were already objects selected on the canvas and the selection was changed.
     * @param event
     */
    eventSelectionUpdated(event) {

        this.setCurrentSelectedObjects(
            this.getCanvas().getActiveObjects().map(fabricObject => fabricObject.id)
        );

        this.render();

        if (this.getCanvas().getActiveObjects().length === 1) {
            if (this.getCanvas().getActiveObjects()[0] instanceof fabric.Image) {
                this.getCanvas().getActiveObjects()[0].set({opacity: 0.7});
            }
        }

        CanvasDebug.selection.updated({
            event,
            description: 'Selection of objects on the canvas changed.',
            currentSelectedObjects: this.getCurrentSelectedObjects()
        });

    }

    /**
     * Use the values in the FabricObjects to update the values in the CanvasObjects instances.
     * @param event
     */
    eventCanvasObjectsModified(event) {

        CanvasDebug.objects.modified({
            event
        });

        // Remove selection as multiselection will change values. This way the FabricObjects again contain values relative to the canvas.
        this.getCanvas().discardActiveObject();
        this.getCanvas().renderAll();

        this.getCanvasObjects().forEach(canvasObject => canvasObject.readFromFabricObject());

        this.render();

    }

    /**
     * Scrolling with the mouse wheel changes the view in the canvas.
     * @param opt
     */
    eventScrollToZoom(opt) {

        if (configuration.getConfig().template.simple === true) {
            return;
        }
        const delta = opt.e.deltaY;
        const pointer = this.getCanvas().getPointer(opt.e);
        let zoom = this.getCanvas().getZoom();
        zoom = zoom + delta / 200;
        if (zoom > 20) zoom = 20;
        if (zoom < 0.01) zoom = 0.01;
        this.getCanvas().zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom);
        opt.e.preventDefault();
        opt.e.stopPropagation();
    }

    eventMouseUp(event, mouseState) {

        if (window['w2p_mouse_mod'] === 1) {
            return;
        }

        mouseState.upPosition = event.pointer;
        let xDiffAbs = Math.abs(mouseState.downPosition.x - mouseState.upPosition.x);
        let yDiffAbs = Math.abs(mouseState.downPosition.y - mouseState.upPosition.y);
        let panX = Math.min(mouseState.upPosition.x, mouseState.downPosition.x);
        let panY = Math.min(mouseState.upPosition.y, mouseState.downPosition.y);
        let xDiff = mouseState.downPosition.x - mouseState.upPosition.x;
        let yDiff = mouseState.downPosition.y - mouseState.upPosition.y;
        let canvas = this.getCanvas();

        if (window['w2p_mouse_mod'] === 2 && xDiffAbs > 10 && yDiffAbs > 10) {

            if (mouseState.isZoom) {
                canvas.absolutePan({x:0, y:0});
                canvas.setZoom(1);
                canvas.requestRenderAll();
                mouseState.isZoom = false;
                return;
            }

            let zoom = 2;
            zoom = this.getWidth() / xDiffAbs;
            //canvas.setZoom(1);  // reset zoom so pan actions work as expected
            let vpw = canvas.width / zoom;
            let vph = canvas.height / zoom;
            let x = (panX - vpw / 2);  // x is the location where the top left of the viewport should be
            let y = (panY - vph / 2);  // y idem
            canvas.absolutePan({x: panX, y: panY});
            canvas.setZoom(zoom);
            canvas.requestRenderAll();
            mouseState.isZoom = true;
        } else if (window['w2p_mouse_mod'] === 3) {

            let delta = new fabric.Point(xDiff,yDiff) ;
            canvas.relativePan(delta);
            canvas.requestRenderAll();

        }

        mouseState.isDown = false;

    }

    convertPixelToMm(valueInPixel) {
        return valueInPixel / (this._factor || 1);
    }

    convertMmInPixel(valueInMm) {
        return valueInMm * (this._factor || 1);
    }

    // Getter & Setter Methods

    getFactor() {
        return this._factor;
    }

    setFactor(value) {
        this._factor = value;
    }

    getDpi() {
        return this._dpi;
    }

    getWidth() {
        return this.getCanvas().width;
    }

    getHeight() {
        return this.getCanvas().height;
    }

    getOnlineId() {
        return this._onlineId;
    }

    getViewId() {
        return this._viewId;
    }

    getArticleNumber() {
        return this._articleNumber;
    }

    setArticleNumber(value) {
        this._articleNumber = value;
    }

    getCreateableObjectTypes() {
        return this._createableObjectTypes;
    }

    setCreateableObjectTypes(value) {
        this._createableObjectTypes = value;
    }

    getLastSelectedObjects() {
        return this._lastSelectedObjects;
    }

    setLastSelectedObjects(value) {
        this._lastSelectedObjects = value;
    }

    getCurrentSelectedObjects() {
        return this._currentSelectedObjects;
    }

    setCurrentSelectedObjects(value) {
        this._currentSelectedObjects = value;
    }

    getCanvasObjects() {
        return this._canvasObjects.sort((a, b) => a.getZPosition() - b.getZPosition());
    }

    getNumberOfCanvasObjects() {
        return this._canvasObjects.length;
    }

    setCanvasObjects(value) {
        this._canvasObjects = value;
    }

    getBoundingBox() {
        return this._boundingBox;
    }

    getCanvas() {
        return this._canvas;
    }

    getCanAddElements() {
        return this._canAddElements;
    }

    setCanAddElements(value) {
        this._canAddElements = value;
    }

    getCanSortElements() {
        return this._canSortElements;
    }

    setCanSortElements(value) {
        this._canSortElements = value;
    }

}

export default Canvas;
